import React, { useEffect, useState } from "react";

import { CopyOutlined, KeyOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Flex,
  message,
  Modal,
  Popconfirm,
  theme,
  Typography,
} from "antd";

import { getErrorMessage } from "@/utilities";

import { fecha } from "./agent-config";
import { agentApi, WorkerKeyCreated, WorkerKeyStatus } from "./api";
import { avisoDeCredencial, envParaSleakOps } from "./asistentes";

const { Text, Paragraph } = Typography;

const copiar = (texto: string, que: string) =>
  void navigator.clipboard
    .writeText(texto)
    .then(() => message.success(`${que} copiada`))
    .catch(() => message.error("No se pudo copiar"));

interface Props {
  storeCode: string;
  editable: boolean;
}

/**
 * La credencial del worker (D63): una clave por store con la que el pod entra y pide una sesión por
 * asistente. Se ve una sola vez, en el modal, con lo que va en SleakOps; después sólo cómo termina.
 * La clave vive en el estado del modal y se suelta al cerrarlo: no se guarda en ningún lado.
 */
export const CredencialDelWorker: React.FC<Props> = ({
  storeCode,
  editable,
}) => {
  const { token } = theme.useToken();
  const [estado, setEstado] = useState<WorkerKeyStatus | null>(null);
  const [disponible, setDisponible] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [nueva, setNueva] = useState<WorkerKeyCreated | null>(null);

  const leer = () =>
    agentApi
      .credencial(storeCode)
      .then((k) => {
        setEstado(k);
        setDisponible(true);
      })
      // Sin la ruta (una API anterior a D63) la tarjeta lo dice y no rompe la página.
      .catch((e) => {
        if (e?.response?.status === 404) setDisponible(false);
        else message.error(getErrorMessage(e));
      });

  useEffect(() => {
    setCargando(true);
    leer().finally(() => setCargando(false));
  }, [storeCode]);

  const generar = async () => {
    setGenerando(true);
    try {
      setNueva(await agentApi.generarCredencial(storeCode));
      await leer();
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setGenerando(false);
    }
  };

  const aviso = avisoDeCredencial(estado);
  const env = nueva ? envParaSleakOps(storeCode, nueva.key) : "";

  return (
    <Card size="small" title="Credencial del worker" loading={cargando}>
      {!disponible ? (
        <Text type="secondary">
          Todavía no está disponible en este ambiente.
        </Text>
      ) : (
        <Flex vertical gap={12}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            El pod de los asistentes entra con esta clave, una por Store, y pide
            una sesión por cada asistente. Va en las variables del workload en
            SleakOps: sumar un asistente no la cambia.
          </Paragraph>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
            <Descriptions.Item label="Estado">
              <Badge
                status={estado?.exists ? "success" : "default"}
                text={estado?.exists ? "Generada" : "Sin generar"}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Termina en">
              {estado?.hint ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Generada">
              {fecha(estado?.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Último uso">
              {estado?.exists
                ? estado.lastUsedAt
                  ? fecha(estado.lastUsedAt)
                  : "Nunca"
                : "—"}
            </Descriptions.Item>
          </Descriptions>
          {editable && (
            <div>
              <Popconfirm
                title={aviso.titulo}
                description={
                  <div style={{ maxWidth: 360 }}>{aviso.descripcion}</div>
                }
                okText={aviso.boton}
                okButtonProps={{ danger: !!estado?.exists }}
                cancelText="Cancelar"
                onConfirm={generar}
              >
                <Button
                  icon={<KeyOutlined />}
                  loading={generando}
                  danger={!!estado?.exists}
                >
                  {estado?.exists ? "Rotar credencial" : "Generar credencial"}
                </Button>
              </Popconfirm>
            </div>
          )}
        </Flex>
      )}

      <Modal
        open={!!nueva}
        title="Credencial del worker"
        maskClosable={false}
        destroyOnClose
        onCancel={() => setNueva(null)}
        footer={
          <Button type="primary" onClick={() => setNueva(null)}>
            Ya la cargué
          </Button>
        }
      >
        {nueva && (
          <Flex vertical gap={12}>
            <Alert
              type="warning"
              showIcon
              message="Se muestra una sola vez"
              description="Copiala ahora: después sólo se ve cómo termina. Si la perdés, generá otra."
            />
            <Flex vertical gap={4}>
              <Text strong>Clave</Text>
              <Flex gap={8} align="center">
                <Text code style={{ wordBreak: "break-all", flex: 1 }}>
                  {nueva.key}
                </Text>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copiar(nueva.key, "Clave")}
                >
                  Copiar
                </Button>
              </Flex>
            </Flex>
            <Flex vertical gap={4}>
              <Text strong>Para SleakOps</Text>
              <Text type="secondary">
                Pegalo en las variables del workload del asistente, en lugar de
                AGENT_USER y AGENT_PASSWORD_HASH, y volvé a deployar.
              </Text>
              <pre
                style={{
                  margin: 0,
                  padding: 12,
                  borderRadius: token.borderRadius,
                  background: token.colorFillTertiary,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  fontSize: 12,
                }}
              >
                {env}
              </pre>
              <div>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copiar(env, "Configuración")}
                >
                  Copiar todo
                </Button>
              </div>
            </Flex>
          </Flex>
        )}
      </Modal>
    </Card>
  );
};
